'use client';

import { c as _c } from "react/compiler-runtime";
import type { SavedViewSummary } from '@repo/schema/table-configuration';
import { createContext, type PropsWithChildren, use, useRef, useState } from 'react';

/** `null` lists every view, `'new'` saves the live table as a new view, a view shows its page. */
export type PanelPage = 'new' | null | SavedViewSummary;

/** What a view's page opens on: `Delete` starts on the delete warning. */
export type ViewPageSection = 'Delete';
type PanelState = null | {
  section?: ViewPageSection;
  view: PanelPage;
};
const SavedViewsPanelContext = createContext<null | {
  close: () => void;
  open: (_view?: PanelPage, _section?: ViewPageSection) => void;
  panel: PanelState;
  /** Shows `to` only while the panel still shows `from`, the new view page or a view's id. */
  replace: (_from: 'new' | number, _to: PanelPage) => void;
}>(null);
const shows = (page: PanelPage, from: 'new' | number) => from === 'new' ? page === 'new' : page !== null && page !== 'new' && page.id === from;

/**
 * The Saved Views panel's state, so the sidebar and the toolbar both open it. Closing returns
 * focus to whatever opened it.
 */
export const SavedViewsPanelProvider = t0 => {
  const $ = _c(10);
  const {
    children
  } = t0;
  const [panel, setPanel] = useState(null);
  const opener = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setPanel(null);
      opener.current?.focus();
      opener.current = null;
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== panel) {
    t2 = (t3, section) => {
      const view = t3 === undefined ? null : t3;
      if (!panel && document.activeElement instanceof HTMLElement) {
        opener.current = document.activeElement;
      }
      setPanel({
        section,
        view
      });
    };
    $[1] = panel;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (from, to) => setPanel(current => current && shows(current.view, from) ? {
      view: to
    } : current);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== panel || $[5] !== t2) {
    t4 = {
      close: t1,
      open: t2,
      panel,
      replace: t3
    };
    $[4] = panel;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children || $[8] !== t4) {
    t5 = <SavedViewsPanelContext value={t4} data-sentry-element="SavedViewsPanelContext" data-sentry-component="SavedViewsPanelProvider" data-sentry-source-file="saved-views-panel.tsx">{children}</SavedViewsPanelContext>;
    $[7] = children;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export const useSavedViewsPanel = () => {
  const value = use(SavedViewsPanelContext);
  if (!value) throw new Error('useSavedViewsPanel must be used inside SavedViewsPanelProvider');
  return value;
};